import React from 'react';

// Simple animated progress bar
export function BarRow({ label, value, max = 100, color = '#6366F1', suffix = '' }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="flex items-center gap-3">
      <div className="w-40 shrink-0 text-sm text-slate-600 truncate">{label}</div>
      <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-700 ease-out"
          style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="w-14 shrink-0 text-end text-sm font-semibold text-slate-800">{value}{suffix}</div>
    </div>
  );
}

// Donut chart from segments [{value, color, label}]
export function Donut({ segments, size = 160, stroke = 22, centerLabel, centerSub }) {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F1F5F9" strokeWidth={stroke} />
        {segments.map((s, i) => {
          const len = (s.value / total) * c;
          const el = (
            <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.color}
              strokeWidth={stroke} strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset}
              strokeLinecap="butt" className="transition-all duration-700" />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-2xl font-extrabold text-slate-900">{centerLabel}</span>
        {centerSub && <span className="text-xs text-slate-400">{centerSub}</span>}
      </div>
    </div>
  );
}

// Scatter plot value (y) vs absorption (x)
export function Scatter({ points, height = 260 }) {
  const pad = 34;
  const w = 520;
  const h = height;
  const x = (v) => pad + (v / 100) * (w - pad * 2);
  const y = (v) => h - pad - (v / 100) * (h - pad * 2);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
      {/* quadrant shading */}
      <rect x={x(50)} y={pad} width={w - pad - x(50)} height={y(50) - pad} fill="#EEF2FF" opacity="0.7" />
      {[0, 25, 50, 75, 100].map((g) => (
        <g key={g}>
          <line x1={x(g)} y1={pad} x2={x(g)} y2={h - pad} stroke="#F1F5F9" />
          <line x1={pad} y1={y(g)} x2={w - pad} y2={y(g)} stroke="#F1F5F9" />
        </g>
      ))}
      <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#CBD5E1" />
      <line x1={pad} y1={pad} x2={pad} y2={h - pad} stroke="#CBD5E1" />
      {points.map((p, i) => (
        <circle key={i} cx={x(p.absorption)} cy={y(p.value)} r={5}
          fill={p.color} opacity="0.75" stroke="#fff" strokeWidth="1">
          <title>{`${p.title} — value ${p.value}, absorption ${p.absorption}`}</title>
        </circle>
      ))}
      <text x={w / 2} y={h - 6} textAnchor="middle" className="fill-slate-400" fontSize="11">Absorption →</text>
      <text x={12} y={h / 2} textAnchor="middle" transform={`rotate(-90 12 ${h / 2})`} className="fill-slate-400" fontSize="11">Value →</text>
    </svg>
  );
}
